import React, { useCallback, useEffect, useState } from "react";
import { Monitor, Smartphone, LogOut, RefreshCw, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { licenseApi } from "../lib/api";

const PLATFORM_META = {
  web: { label: "Web", Icon: Monitor },
  mobile: { label: "Mobil", Icon: Smartphone },
};

const fmtSeen = (iso) => {
  if (!iso) return "bilinmiyor";
  try {
    const d = new Date(iso);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "az önce";
    if (mins < 60) return `${mins} dakika önce`;
    if (mins < 1440) return `${Math.floor(mins / 60)} saat önce`;
    return d.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const ActiveSessions = ({ onSelfLogout }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await licenseApi.sessions();
      setSessions(data.sessions || []);
    } catch (e) {
      toast.error("Oturumlar alınamadı");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const terminate = async (platform) => {
    setBusy(platform);
    try {
      const res = await licenseApi.terminateSession(platform);
      setConfirming(null);
      if (res.self_terminated) {
        toast.success("Bu cihazın oturumu kapatıldı — çıkış yapılıyor");
        if (onSelfLogout) onSelfLogout();
        return;
      }
      toast.success("Oturum kapatıldı");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Oturum kapatılamadı");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="active-sessions-panel">
      <div className="flex items-center justify-between">
        <div className="hud-text text-sertex-cyan flex items-center gap-1">
          <ShieldAlert className="h-3 w-3" /> AÇIK CİHAZLAR
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="hud-text text-sertex-textMuted hover:text-sertex-cyan flex items-center gap-1 transition-colors"
          data-testid="sessions-refresh-btn"
        >
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          YENİLE
        </button>
      </div>

      <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[11px]">
        Her platform için tek oturum açık kalabilir: 1 web + 1 mobil. Telefonunu
        kaybettiysen mobil oturumu buradan kapatabilirsin.
      </div>

      {sessions.length === 0 && !loading && (
        <div
          className="glass-panel p-3 border-sertex-cyan/20 text-[11px] font-mono text-sertex-textMuted"
          data-testid="sessions-empty"
        >
          Açık oturum bulunamadı.
        </div>
      )}

      <div className="space-y-2">
        {sessions.map((s) => {
          const meta = PLATFORM_META[s.platform] || PLATFORM_META.web;
          const { Icon } = meta;
          return (
            <div
              key={s.platform}
              className="glass-panel p-3 border-sertex-cyan/20 flex items-center gap-3"
              data-testid={`session-row-${s.platform}`}
            >
              <Icon className="h-4 w-4 text-sertex-cyan shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm text-sertex-text truncate">
                    {s.device_label}
                  </span>
                  <span className="hud-text text-sertex-textMuted px-1.5 py-0.5 border border-sertex-cyan/25 rounded">
                    {meta.label}
                  </span>
                  {s.is_current && (
                    <span
                      className="hud-text text-green-300 px-1.5 py-0.5 border border-green-400/40 rounded bg-green-500/10"
                      data-testid={`session-current-badge-${s.platform}`}
                    >
                      BU CİHAZ
                    </span>
                  )}
                </div>
                <div className="text-[10px] font-mono text-sertex-textMuted mt-0.5">
                  Son etkinlik: {fmtSeen(s.last_seen_at)}
                </div>
              </div>

              {confirming === s.platform ? (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => terminate(s.platform)}
                    disabled={busy === s.platform}
                    className="px-2 py-1 border border-red-400/60 bg-red-500/15 hover:bg-red-500/25 rounded hud-text text-red-300 flex items-center gap-1 transition-colors"
                    data-testid={`session-confirm-${s.platform}`}
                  >
                    {busy === s.platform ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <LogOut className="h-3 w-3" />
                    )}
                    EMİNİM, KAPAT
                  </button>
                  <button
                    onClick={() => setConfirming(null)}
                    className="px-2 py-1 border border-sertex-cyan/30 hover:bg-sertex-cyan/10 rounded hud-text text-sertex-textMuted transition-colors"
                    data-testid={`session-cancel-${s.platform}`}
                  >
                    VAZGEÇ
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirming(s.platform)}
                  className="px-2 py-1 border border-red-400/40 hover:bg-red-500/15 rounded hud-text text-red-300/90 shrink-0 flex items-center gap-1 transition-colors"
                  data-testid={`session-terminate-${s.platform}`}
                >
                  <LogOut className="h-3 w-3" />
                  {s.is_current ? "ÇIKIŞ YAP" : "OTURUMU KAPAT"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {confirming && (
        <div
          className="text-[11px] font-mono text-red-300/90"
          data-testid="sessions-confirm-hint"
        >
          {confirming === "web" || confirming === "mobile"
            ? `Bu işlem ${PLATFORM_META[confirming]?.label || confirming} oturumunu hemen sonlandırır.`
            : ""}
        </div>
      )}
    </div>
  );
};

export default ActiveSessions;
